import { useState } from 'react';
import { Divider, Menu, MenuItem } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { logout } from '../../auth/authActions';
import ImpersonationSearchDialog from '../impersonation/ImpersonationSearchDialog';
import ImpersonationConfirmDialog from '../impersonation/ImpersonationConfirmDialog';
import { UserSearchResult } from '../../../api/users';
import styles from './profile-menu.module.scss';
import { HELP_GUIDANCE_URL } from '../../pages/user-profile/UserProfile';

type ProfileMenuProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  user: any;
};

const ProfileMenu = ({ anchorEl, open, onClose, user }: ProfileMenuProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isSuperAdmin = user?.profile?.name === 'SuperAdmin';
  const [searchOpen, setSearchOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<UserSearchResult | null>(null);

  const handleImpersonate = () => {
    onClose();
    setSearchOpen(true);
  };

  const handleLogout = () => {
    onClose();
    dispatch(logout());
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { className: styles.menuPaper } }}
      >
        <div className={styles.header}>
          <div className={styles.name}>{user?.name}</div>
          <div className={styles.email}>{user?.email}</div>
        </div>

        <Divider />

        <MenuItem className={styles.item} component={Link} to="/user-profile" onClick={onClose}>
          {t('profileMenu.myProfile', 'My profile')}
        </MenuItem>

        <MenuItem
          className={styles.item}
          component="a"
          href={HELP_GUIDANCE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
        >
          {t('profileMenu.helpSupport', 'Help and Support')}
        </MenuItem>

        {isSuperAdmin && (
          <MenuItem className={styles.item} onClick={handleImpersonate}>
            {t('profileMenu.impersonate', 'Impersonate user…')}
          </MenuItem>
        )}

        <Divider />

        <MenuItem className={`${styles.item} ${styles.signOut}`} onClick={handleLogout}>
          <LogoutIcon fontSize="small" className={styles.signOutIcon} />
          {t('profileMenu.signOut', 'Sign out')}
        </MenuItem>
      </Menu>

      <ImpersonationSearchDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onPick={(targetUser) => {
          setSearchOpen(false);
          setConfirmTarget(targetUser);
        }}
      />
      <ImpersonationConfirmDialog
        target={confirmTarget}
        onCancel={() => setConfirmTarget(null)}
      />
    </>
  );
};

export default ProfileMenu;
